import React, { useRef, useEffect, useState } from "react";
import { Tooltip as MuiTooltip, TooltipProps } from "@material-ui/core";
import styled from "styled-components";
import { smallText, smallTitle } from "../textStyles";
import { Button } from "./Button";

import { themeColors } from "../react/sharedStyles";

interface CustomTooltipProps extends Omit<TooltipProps, "title"> {
    tooltipTitle?: string;
    tooltipText?: string;
    onUpgrade?: () => void;
}

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
))`
    & .MuiTooltip-tooltip {
        ${smallText}
        max-width: 198px;
        padding: 0px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-color: ${themeColors.darkGray};
        color: #fff;
        text-align: left;
        line-height: 130%;
        border-radius: 4px;
    }

    & .MuiTooltip-arrow {
        color: ${themeColors.darkGray};
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 8px 12px 12px 12px;
`;

const TooltipTitle = styled.div`
    ${smallTitle}
    text-transform: none;
    color: #fff;
`;

const TooltipText = styled.div`
    ${smallText}
    color: ${themeColors.lightGray};
    margin-top: 8px;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
    padding: 0px 12px 8px 12px;
    justify-content: center;
    width: 100%;
`;

const StyledUpgradeButton = styled(Button)`
    padding: 6px 12px;
`;

export const UpgradeTooltip = ({
    children,
    tooltipTitle,
    tooltipText,
    onUpgrade,
    ...props
}: CustomTooltipProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const wrappedChildren = React.cloneElement(
        children as React.ReactElement,
        {
            onMouseEnter: (e: React.MouseEvent) => {
                setIsOpen(true);
                const existingOnMouseEnter = (children as React.ReactElement).props.onMouseEnter;
                if (existingOnMouseEnter) {
                    existingOnMouseEnter(e);
                }
            }
        }
    );

    let tooltipContent = (
        <Content>
            {tooltipTitle && (
                <TooltipTitle>
                    {tooltipTitle}
                </TooltipTitle>
            )}
            {tooltipText && (
                <TooltipText>
                    {tooltipText}
                </TooltipText>
            )}
        </Content>
    );

    if (onUpgrade) {
        tooltipContent = (
            <>
                <Content>
                    {tooltipTitle && (
                        <TooltipTitle>
                            {tooltipTitle}
                        </TooltipTitle>
                    )}
                    {tooltipText && (
                        <TooltipText>
                            {tooltipText}
                        </TooltipText>
                    )}
                </Content>
                <Actions>
                    <StyledUpgradeButton
                        upgrade
                        onClick={onUpgrade}
                        width="100%"
                    >
                        Upgrade
                    </StyledUpgradeButton>
                </Actions>
            </>
        );
    }

    return (
        <StyledTooltip
            {...props}
            title={tooltipContent}
            interactive={!!onUpgrade}
            open={isOpen}
            onClose={() => setIsOpen(false)}
            onMouseLeave={() => setIsOpen(false)}
            PopperProps={{
                ...props.PopperProps,
                ref: tooltipRef
            }}
        >
            {wrappedChildren}
        </StyledTooltip>
    );
};
